import { Avatar, CommandPalette, type PaletteGroup, type PaletteItem } from "@helmr/ui";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { searchLeads, searchMembers } from "../api/lists";
import { AREAS } from "./nav";
import { useAppContext, useSessionSnapshot } from "./permissions";
import { useServices } from "./services";
import { strings } from "./strings";

const t = strings.shell;

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return v;
}

/** ⌘K: navigation filtered by permission, plus members and leads from the API (debounced 200ms, 8 results each). */
export function SearchPalette({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const { client } = useServices();
  const ctx = useAppContext();
  const sess = useSessionSnapshot();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  useEffect(() => { if (!open) setQuery(""); }, [open]);

  const raw = query.trim();
  const q = useDebounced(raw, 200);
  const settled = q === raw;
  const canMembers = ctx.has("members.view");
  const canLeads = ctx.has("leads.view");
  const searching = open && q.length >= 2;

  const members = useQuery({
    queryKey: ["search", "members", sess.businessId, sess.branchId, q],
    queryFn: ({ signal }) => searchMembers(client, q, signal),
    enabled: searching && canMembers,
    staleTime: 15_000,
  });
  const leads = useQuery({
    queryKey: ["search", "leads", sess.businessId, sess.branchId, q],
    queryFn: ({ signal }) => searchLeads(client, q, signal),
    enabled: searching && canLeads,
    staleTime: 15_000,
  });

  const pages: PaletteItem[] = AREAS.filter((a) => a.allow(ctx.has) && a.label.toLowerCase().includes(raw.toLowerCase())).map((a) => ({
    id: `page-${a.key}`,
    label: a.label,
    icon: a.icon,
    hint: a.key === "home" ? undefined : a.path,
    onSelect: () => navigate(a.path),
  }));

  const groups: PaletteGroup[] = [{ id: "pages", heading: t.pages, items: pages }];
  if (canMembers && raw.length >= 2) {
    groups.push({
      id: "members", heading: t.members, loading: !settled || members.isFetching,
      items: (members.data ?? []).slice(0, 8).map((m) => ({ id: `member-${m.id}`, label: m.name, hint: m.phone ?? undefined, lead: <Avatar id={m.id} name={m.name} size="sm" />, onSelect: () => navigate(`/members/${m.id}`) })),
    });
  }
  if (canLeads && raw.length >= 2) {
    groups.push({
      id: "leads", heading: t.leads, loading: !settled || leads.isFetching,
      items: (leads.data ?? []).slice(0, 8).map((l) => ({ id: `lead-${l.id}`, label: l.name ?? l.phone ?? l.id, hint: l.name ? (l.phone ?? undefined) : undefined, lead: <Avatar id={l.id} name={l.name ?? "?"} size="sm" />, onSelect: () => navigate(`/leads/${l.id}`) })),
    });
  }

  return <CommandPalette open={open} onOpenChange={onOpenChange} query={query} onQueryChange={setQuery} groups={groups} placeholder={t.searchLong} label={t.search} emptyText={t.noResults} />;
}
